import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  useAllTenantCompaniesQuery,
  useAllTenantFleetsQuery,
  useRunProfilesQuery,
  useSaveTenantScheduleMutation,
  useTenantScheduleQuery
} from '../../services/routing/queries';
import { namedCompany, namedFleet, namesById } from '../../utils/format';
import type { RunProfile } from '../../types/routing';

/** The schedule stores a plain HH:mm:ss; the browser's time input wants HH:mm. */
const toInputTime = (stored: string) => stored.slice(0, 5);

const NONE = '';

const WATCH_FROM_DEFAULT = '20:00';
const WATCH_MIN_RIDES_DEFAULT = '150';
const WATCH_QUIET_DEFAULT = '5';

const isTime = (value: string) => /^\d{2}:\d{2}(:\d{2})?$/.test(value.trim());
const isWholeNumber = (value: string) => /^\d+$/.test(value.trim());

/**
 * What the profile plans, in one line — the fleet order first, because that is its point. Named
 * rather than listed by id: this line exists to say what tonight plans, and "0" says nothing. An
 * id whose name has not arrived — or whose fleet is gone — falls back to a labelled id, so the
 * recap never reads blank waiting for a listing.
 */
const summary = (
  profile: RunProfile,
  fleetNames: Map<number, string>,
  companyNames: Map<number, string>
) => {
  const fleets = (ids: number[]) => ids.map(id => namedFleet(id, fleetNames)).join(', ');
  const order = profile.fleet_groups.length
    ? profile.fleet_groups.map(fleets).join('  →  ')
    : 'no fleet order — every fleet plans together';
  const scope = profile.fleet_ids.length ? `fleets ${fleets(profile.fleet_ids)}` : 'every fleet';
  const companies = profile.company_ids.length
    ? `companies ${profile.company_ids.map(id => namedCompany(id, companyNames)).join(', ')}`
    : "every company's rides";
  return `${scope} · ${companies} · order: ${order}`;
};

/**
 * A tenant's nightly run: whether it fires, at what New York time, how far ahead it plans, and
 * the run profile it plans. The profile is referenced, not copied — editing it on the profiles
 * panel changes what fires tonight, which is why this screen only picks one. The arrival watch
 * lets the night start before its time once the watched companies have delivered their rides.
 */
const Panel = ({ tenant }: { tenant: string }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: schedule, isLoading, isError, error } = useTenantScheduleQuery(tenant);
  const save = useSaveTenantScheduleMutation(tenant);

  const profileData = useRunProfilesQuery(tenant).data;
  const profiles = useMemo(() => profileData?.profiles ?? [], [profileData]);
  // The same two listings the profiles panel above already reads, so on this page they cost
  // nothing — they are only here to turn the profile's ids into the names it means.
  const fleetData = useAllTenantFleetsQuery(tenant).data;
  const companyData = useAllTenantCompaniesQuery(tenant).data;
  const fleetNames = useMemo(() => namesById(fleetData ?? []), [fleetData]);
  const companyNames = useMemo(() => namesById(companyData ?? []), [companyData]);

  const [enabled, setEnabled] = useState(false);
  const [atTime, setAtTime] = useState('22:00');
  const [offset, setOffset] = useState('1');
  const [profileId, setProfileId] = useState<string>(NONE);
  const [adminId, setAdminId] = useState('');
  const [planOnRefusal, setPlanOnRefusal] = useState(false);
  const [watching, setWatching] = useState(false);
  const [watchFrom, setWatchFrom] = useState(WATCH_FROM_DEFAULT);
  const [watchCompanies, setWatchCompanies] = useState<string[]>([]);
  const [watchMinRides, setWatchMinRides] = useState(WATCH_MIN_RIDES_DEFAULT);
  const [watchQuiet, setWatchQuiet] = useState(WATCH_QUIET_DEFAULT);

  // Cleared the moment the tenant changes, so a half-second of the previous tenant's schedule
  // can never be looking back at someone — or, worse, be saved onto the new one.
  useEffect(() => {
    setEnabled(false);
    setAtTime('22:00');
    setOffset('1');
    setProfileId(NONE);
    setAdminId('');
    setPlanOnRefusal(false);
    setWatching(false);
    setWatchFrom(WATCH_FROM_DEFAULT);
    setWatchCompanies([]);
    setWatchMinRides(WATCH_MIN_RIDES_DEFAULT);
    setWatchQuiet(WATCH_QUIET_DEFAULT);
  }, [tenant]);

  useEffect(() => {
    if (!schedule || schedule.tenant !== tenant) return;
    setEnabled(schedule.enabled);
    setAtTime(toInputTime(schedule.at_time));
    setOffset(String(schedule.plan_day_offset));
    setProfileId(schedule.profile_id == null ? NONE : String(schedule.profile_id));
    setAdminId(schedule.requested_by_admin_id == null ? '' : String(schedule.requested_by_admin_id));
    setPlanOnRefusal(schedule.plan_when_start_callback_fails);
    setWatching(schedule.watch_from_time != null);
    setWatchFrom(schedule.watch_from_time == null
      ? WATCH_FROM_DEFAULT
      : toInputTime(schedule.watch_from_time));
    setWatchCompanies(schedule.watch_company_ids.map(String));
    setWatchMinRides(String(schedule.watch_min_rides));
    setWatchQuiet(String(schedule.watch_quiet_minutes));
  }, [schedule, tenant]);

  // The form shows defaults when the GET fails, and PUTting those would disable a schedule that
  // is running perfectly well and throw its profile away. Nothing is saved until the current
  // tenant's schedule has actually been read.
  const loaded = !!schedule && schedule.tenant === tenant;

  // Both of these fields fail the same way when emptied: the value the API then applies is not
  // the value on screen, so saving an unrelated setting silently moves the schedule. An empty
  // offset reads as 0 (Number('') is 0) and turns a next-day schedule into a same-day one; an
  // empty time falls back to the API's 22:00 default and moves when the run fires. Neither is
  // saveable.
  const offsetValid = isWholeNumber(offset) && Number(offset) >= 0 && Number(offset) <= 7;
  const timeValid = isTime(atTime);
  // Said here rather than left to the API's 422: an enabled night with no selection has nothing
  // to plan.
  const profileValid = !enabled || profileId !== NONE;
  // The watch runs from its start to the firing time, so a start that is not before it is a
  // watch that never runs; and one waiting for nobody could never be satisfied.
  const watchFromValid = isTime(watchFrom) && (!timeValid || watchFrom.trim() < atTime.trim());
  const watchCompaniesValid = watchCompanies.length > 0;
  const watchMinRidesValid = isWholeNumber(watchMinRides);
  const watchQuietValid =
    isWholeNumber(watchQuiet) && Number(watchQuiet) >= 1 && Number(watchQuiet) <= 120;
  const watchValid =
    !watching || (watchFromValid && watchCompaniesValid && watchMinRidesValid && watchQuietValid);
  const saveable = loaded && offsetValid && timeValid && profileValid && watchValid;

  const picked = profiles.find(profile => String(profile.profile_id) === profileId)
    // A profile named by the schedule but not in the list is one the tenant no longer has; the
    // schedule inlines what it could resolve, so fall back to that before showing nothing.
    ?? (schedule?.profile && String(schedule.profile.profile_id) === profileId
      ? schedule.profile
      : undefined);

  const onSave = () => {
    if (!saveable) return;
    save.mutate(
      {
        enabled,
        at_time: atTime,
        watch_from_time: watching ? watchFrom : null,
        watch_company_ids: watching ? watchCompanies.map(Number) : [],
        watch_min_rides: Number(watchMinRides),
        watch_quiet_minutes: Number(watchQuiet),
        plan_day_offset: Number(offset),
        plan_when_start_callback_fails: planOnRefusal,
        profile_id: profileId === NONE ? null : Number(profileId),
        requested_by_admin_id: adminId ? Number(adminId) : null
      },
      {
        onSuccess: () => enqueueSnackbar('Nightly schedule saved'),
        onError: failure => enqueueSnackbar((failure as Error).message, { variant: 'error' })
      }
    );
  };

  const firingTime = timeValid ? atTime : 'the firing time';

  const plannedDay = !offsetValid
    ? 'whole days, 0 to 7'
    : Number(offset) === 0
      ? 'the day it fires'
      : `${offset} day(s) after it fires`;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        NIGHTLY SCHEDULE
      </Typography>
      {isError ? (
        <Alert severity="error">
          Could not read the schedule for &ldquo;{tenant}&rdquo; ({(error as Error).message}).
          Saving is disabled until it loads — otherwise this form would write its defaults over
          whatever is really there.
        </Alert>
      ) : isLoading ? (
        <Typography variant="body2" color="text.secondary">
          loading&#8230;
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={event => setEnabled(event.target.checked)}
                  inputProps={{ 'aria-label': 'nightly run enabled' }}
                />
              }
              label={enabled ? 'Runs nightly' : 'Off'}
            />
            <TextField
              label="At (America/New_York)"
              type="time"
              value={atTime}
              onChange={event => setAtTime(event.target.value)}
              error={!timeValid}
              helperText={timeValid ? ' ' : 'a firing time is required'}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <TextField
              label="Plans"
              type="number"
              value={offset}
              onChange={event => setOffset(event.target.value)}
              helperText={plannedDay}
              error={!offsetValid}
              inputProps={{ min: 0, max: 7, 'aria-label': 'plan day offset' }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <TextField
              label="Requested by admin id"
              type="number"
              value={adminId}
              onChange={event => setAdminId(event.target.value)}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
          </Stack>

          {schedule?.last_fired_day && (
            <Alert severity="info" sx={{ py: 0 }}>
              Last fired {schedule.last_fired_day}
              {schedule.last_run_id ? ` — run #${schedule.last_run_id}` : ''}
            </Alert>
          )}

          <TextField
            select
            label="Run profile"
            value={profileId}
            onChange={event => setProfileId(event.target.value)}
            error={!profileValid}
            helperText={
              profileValid
                ? 'read when the run fires, so editing the profile changes what tonight plans'
                : 'an enabled schedule needs a profile — it is what says which fleets and companies the night plans'
            }
            SelectProps={{ SelectDisplayProps: { 'aria-label': 'run profile' } }}
            sx={{ minWidth: { xs: 0, sm: 320 }, maxWidth: 420 }}
          >
            <MenuItem value={NONE}>None</MenuItem>
            {profiles.map(profile => (
              <MenuItem key={profile.profile_id} value={String(profile.profile_id)}>
                {profile.name}
              </MenuItem>
            ))}
          </TextField>

          {picked ? (
            <Typography variant="body2" color="text.secondary">
              {summary(picked, fleetNames, companyNames)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Profiles are saved on the Run profiles panel above.
            </Typography>
          )}

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={planOnRefusal}
                  onChange={event => setPlanOnRefusal(event.target.checked)}
                  inputProps={{ 'aria-label': 'plan when the start callback fails' }}
                />
              }
              label="Plan even if the platform will not clear the day"
            />
            <Typography variant="body2" color="text.secondary">
              {planOnRefusal
                ? 'The refusal is logged and the night is planned anyway — its rides can land on top of the assignments the last plan left in place.'
                : 'A refused routing.started callback fails the run instead: nothing is planned, and the night has to be replanned by hand.'}
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={watching}
                  onChange={event => setWatching(event.target.checked)}
                  inputProps={{ 'aria-label': 'fire early once the vendors have delivered' }}
                />
              }
              label="Fire early once the vendors have delivered"
            />
            <Typography variant="body2" color="text.secondary">
              {watching
                ? "From the watch start the planned day's rides are read every minute. The night "
                  + 'starts as soon as more than the rides needed are in and every watched company '
                  + `has delivered and then gone quiet for the gap — or at ${firingTime} regardless.`
                : `The night fires at ${firingTime} and not before.`}
            </Typography>
            {watching && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              >
                <TextField
                  label="Watch from (America/New_York)"
                  type="time"
                  value={watchFrom}
                  onChange={event => setWatchFrom(event.target.value)}
                  error={!watchFromValid}
                  helperText={
                    watchFromValid
                      ? ' '
                      : isTime(watchFrom)
                        ? 'must be before the firing time'
                        : 'a watch start is required'
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                />
                <TextField
                  select
                  label="Wait for companies"
                  value={watchCompanies}
                  onChange={event => setWatchCompanies(event.target.value as unknown as string[])}
                  error={!watchCompaniesValid}
                  helperText={
                    watchCompaniesValid
                      ? 'each must deliver a ride, then stop for the gap'
                      : 'pick the vendors whose rides the night waits for'
                  }
                  SelectProps={{
                    multiple: true,
                    renderValue: selected =>
                      (selected as string[])
                        .map(id => namedCompany(Number(id), companyNames))
                        .join(', '),
                    SelectDisplayProps: { 'aria-label': 'watched companies' }
                  }}
                  sx={{ minWidth: { xs: 0, sm: 280 }, maxWidth: 420 }}
                >
                  {(companyData ?? []).map(company => (
                    <MenuItem key={company.id} value={String(company.id)}>
                      {namedCompany(company.id, companyNames)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Rides needed"
                  type="number"
                  value={watchMinRides}
                  onChange={event => setWatchMinRides(event.target.value)}
                  error={!watchMinRidesValid}
                  helperText={
                    watchMinRidesValid ? 'fires once more than this many are in' : 'a count is required'
                  }
                  inputProps={{ min: 0, 'aria-label': 'watch min rides' }}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                />
                <TextField
                  label="Quiet minutes"
                  type="number"
                  value={watchQuiet}
                  onChange={event => setWatchQuiet(event.target.value)}
                  error={!watchQuietValid}
                  helperText={watchQuietValid ? 'the gap that ends a delivery' : '1 to 120'}
                  inputProps={{ min: 1, max: 120, 'aria-label': 'watch quiet minutes' }}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                />
              </Stack>
            )}
          </Stack>

          <Stack direction="row">
            <Button
              variant="contained"
              onClick={onSave}
              disabled={save.isPending || !saveable}
            >
              Save schedule
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

/**
 * Memoised on `tenant`, which is its only input. Without this the panel re-renders on every
 * keystroke typed into any other field on the config page — it is a sibling of the parameter and
 * engine forms, so their state changes re-render it — and rebuilding a few hundred fleet and
 * company menu items between keystrokes was enough to make user-event drop characters on a
 * loaded CI box. That surfaced as two unrelated TenantConfigPage tests failing.
 */
export const NightlySchedulePanel = memo(Panel);

export default NightlySchedulePanel;
